import React, { FC, useEffect, useState } from 'react';
import { TextareaProps } from './types';
import { Textarea as ChakraTextarea, Flex, Text } from '@chakra-ui/react';
import { i18n } from '../../../i18n';

const WORD_REGEX = /\S+/g;

const getWordCount = (value: string): number => {
  const words = value.trim().match(WORD_REGEX);
  return words?.length ?? 0;
};

const isMaxOnly = (wordCountExpected: [number, number]): boolean => wordCountExpected[0] === 0;

const isWithinWordCountRange = (wordCount: number, wordCountExpected: number | [number, number]): boolean => {
  if (typeof wordCountExpected === 'number') {
    return wordCount >= wordCountExpected;
  }
  const [first, second] = wordCountExpected;
  if (isMaxOnly([first, second])) {
    return wordCount <= second;
  }
  return wordCount >= Math.min(first, second) && wordCount <= Math.max(first, second);
};

const getExpectedCountLabel = (wordCountExpected: number | [number, number]): string => {
  if (typeof wordCountExpected === 'number') {
    return i18n.t('{{count}}+ required', { count: wordCountExpected });
  }
  const [first, second] = wordCountExpected;
  if (isMaxOnly([first, second])) {
    return i18n.t('{{max}} words maximum', { max: second });
  }
  return i18n.t('{{min}} to {{max}} required', { min: Math.min(first, second), max: Math.max(first, second) });
};

export const Textarea: FC<TextareaProps> = ({ wordCountExpected, onChange, value, defaultValue, ...rest }) => {
  const initialValue = typeof value === 'string' ? value : typeof defaultValue === 'string' ? defaultValue : '';
  const [wordCount, setWordCount] = useState<number>(getWordCount(initialValue));
  const hasWordCountExpectation = typeof wordCountExpected !== 'undefined';
  const isWordCountInRange = hasWordCountExpectation ? isWithinWordCountRange(wordCount, wordCountExpected) : false;

  useEffect(() => {
    if (value !== undefined) {
      setWordCount(getWordCount(typeof value === 'string' ? value : ''));
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWordCount(getWordCount(event.target.value));
    onChange?.(event);
  };

  return (
    <>
      {hasWordCountExpectation ? (
        <Flex alignItems="baseline" justify="flex-end" mb={1}>
          <Text
            aria-live="polite"
            mb={0}
            color={isWordCountInRange ? 'icon-success' : 'icon-error'}
            textStyle="ui-default"
          >
            {i18n.t('{{count}} words', { count: wordCount })}
          </Text>
          <Text mb={0} color="text-muted" mx={2} textStyle="ui-default">
            |
          </Text>
          <Text mb={0} color="text-muted" textStyle="ui-default">
            {getExpectedCountLabel(wordCountExpected)}
          </Text>
        </Flex>
      ) : null}
      <ChakraTextarea value={value} defaultValue={defaultValue} onChange={handleChange} {...rest} />
    </>
  );
};
